import consola from 'consola';
import { encodeBase64Unicode } from '@/lib/base64';
import { ca } from '@/lib/channelAttribution.js';
import { clearSprigSampleKey } from '@/lib/sprigSampling.js';

/**
 * Shared logout logic (Auth0 and legacy). Use this composable to perform logout
 * from any component so behaviour and fixes stay in one place.
 */
export function useLogout() {
  const { $tracking } = useNuxtApp();
  const { t } = useI18n();
  const testingStore = useTestingStore();
  const toastStore = useToastStore();
  const myAccount = useAccount();
  const { useUrl } = useUrls();

  const isAuth0Enabled = testingStore.hasEnabledFeature('web_auth0InStaticweb');

  /**
   * @param {string} [returnUrl] - For non-Auth0 logout, URL to return to after logout.
   *   If omitted, returns to current page unless on an account path (then static base URL).
   */
  const performLogout = async (returnUrl) => {
    $tracking?.onLogoutClick();

    if (isAuth0Enabled) {
      $tracking?.onLogoutClickGtm();
      $tracking?.reset();

      const sessionStore = useSessionStore();

      try {
        const runtimeConfig = useRuntimeConfig();
        let redirectUrl = ca?.getLastChannelAttributionToQuery(runtimeConfig.public.DEFAULT_BASE_URL) ?? null;

        if (redirectUrl?.pathname?.startsWith('/account')) {
          redirectUrl = useUrl.getStaticBaseURL();
        }

        sessionStore.setRedirectUrl(false, false, redirectUrl);

        await myAccount.logout();
      } catch (err) {
        consola.error('### Error during logout:', err);

        toastStore.show({
          message: t('t_ERRORPROCESSINGREQUESTMESSAGE'),
          type: 'error',
        });

        // Even if logout fails, clear local session state and cookies for safety
        const { clearAuthSession } = useClearAuthSession();
        await clearAuthSession();

        window.location.reload();
      }
    } else {
      clearSprigSampleKey();

      const returnTo
        = returnUrl
          ?? (() => {
            const route = useRoute();
            return route.path?.startsWith('/account') ? useUrl.getStaticBaseURL() : window.location.href;
          })();

      setTimeout(() => {
        const base64QueryParam = encodeBase64Unicode(returnTo);
        const logoutParams = { 'return-to': base64QueryParam };
        navigateTo(useUrl.getLogoutPageUrlDynamic(logoutParams), { external: true });
      }, 100);
    }
  };

  return {
    performLogout,
    isAuth0Enabled,
  };
}
